import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import Axios from 'axios';

import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';

import { injectStateService, StateService } from '@/shared/services/StateService';
import DataCacheService from '@/shared/services/DataCacheService';
import AuthService from '@/shared/services/AuthService';
import RelationName from '@/shared/components/RelationName';
import RegistrationModal from '@/shared/components/registration/RegistrationModal';
import { getRegistrationModalPropsForTask } from '@/shared/components/registration/registrationHelpers';
import Button from '@/shared/components/Button';
import { AddIcon } from '@/shared/components/Icons';
import { FaRegClock, FaRegWindowRestore, FaTrashAlt } from 'react-icons/fa';
import { CanCreate, CanDelete } from '@/shared/components/security';
import YesNoModal from '@/shared/components/YesNoModal';
import ScrollableTable from '@/shared/components/table/ScrollableTable';
import ColumnHeader from '@/shared/components/table/ColumnHeader';
import EditableTaskTagSelector from '@/shared/components/editable/EditableTaskTagSelector';
import userStorage from '@/shared/services/userStorage';
import classNames from 'classnames';
import moment from 'moment';
import TaskDetailsModal from '../../tasks/taskDetails/TaskDetailsModal';
import CreateToDoModal from '../../tasks/CreateToDoModal';
import TaskStatusToggle from '../../tasks/TaskStatusToggle';
import messages from '../dashboardMessages';
import FormattedDateShort from '../../../shared/components/FormattedDateShort';

const WithTagsStorageKey = 'to-do-widget-with-tags';

const defaultFilters = {
  deadlineFrom: null,
  deadlineUntil: null,
  mainStatus: [0, 1, 3],
  ownCompanies: [],
  period: 'all',
  relationTypes: [],
  taskLevels: [2], // Todo's
  userId: null,
  withTags: [],
};

const initialParams = {
  ...defaultFilters,
  desc: false,
  page: 0,
  sortKey: 'deadline',
  pageSize: 50,
  filter: {},
  search: {},
};

class TodoListDashboard extends Component {
  state = {
    items: [],
    currentModal: null,
    users: [],
    userId: null,
  };

  componentDidMount() {
    const userId = AuthService.getUserId();
    const users = DataCacheService.getUsers();
    const withTagsFromStorage = userStorage.getItem(WithTagsStorageKey) || [];

    this.setState({
      userId,
      users,
      initialSearchParams: {
        ...initialParams,
        withTags: withTagsFromStorage,
      },
    });
  }

  componentWillUnmount() {
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to leaving page.');
    }
  }

  loadItems = searchParams => {
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to new request.');
    }

    this.cancelToken = Axios.CancelToken.source();
    const { userId } = this.state;
    const { desc, sortKey, page, pageSize, ...otherParams } = searchParams;
    const params = {
      desc,
      sort: sortKey,
      page,
      take: pageSize,
      ...otherParams,
      userId,
    };

    return api.base
      .post(`${config.appUrls.tasks}/api/TaskList/`, params, {
        cancelToken: this.cancelToken.token,
      })
      .then(({ data }) => {
        const { tasks = [], total = 0 } = data || {};
        this.setState(({ items }) => {
          if (params.page === 0) {
            return {
              items: tasks,
            };
          }

          if (items.length > params.page * params.take) {
            // When a todo is created it is appended to the list.
            // But if you scroll down, it could also be in the next page request
            // So if there is a duplicate todo, we keep the one from the request because it is in the right order (sorting)
            const newItems = items.filter(x => tasks.findIndex(t => t.id === x.id) < 0);
            return {
              items: [...newItems, ...tasks],
            };
          }
          return {
            items: [...items, ...tasks],
          };
        });
        return tasks;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
      });
  };

  openModal = modal => this.setState({ currentModal: modal });

  handleUpdateTasks = updatedTasks => {
    if (!updatedTasks) return;
    this.setState(({ items }) => ({
      items: items.map(row => {
        const updated = updatedTasks.find(x => x.id === row.id);
        if (!updated) return row;
        return { ...row, ...updated };
      }),
    }));
  };
  deleteTodo = taskId => {
    if (!taskId) return;
    const { intl } = this.props;

    return api.base
      .delete(`${config.appUrls.tasks}/api/PlannedTasks/${taskId}`, { params: { taskLevel: 2 } })
      .then(({ data }) => {
        this.setState(({ items }) => ({
          currentModal: null,
          items: items.filter(x => x.id !== taskId),
        }));
        return data;
      }, handleHttpError(intl));
  };

  onToDoCreated = toDo => {
    this.setState(({ items, userId }) => ({
      currentModal: null,
      items: toDo.userId && toDo.userId === userId ? [...items, toDo] : items,
    }));
  };

  onCloseTaskDetail = tasks => {
    if (tasks && tasks.length) {
      this.handleUpdateTasks(tasks);
    }
    this.setState({ currentModal: null });
  };

  openTaskList = () => {
    const { stateService, intl } = this.props;
    const { userId, initialSearchParams } = this.state;

    stateService.gotoState('taskflow.tasklist', {
      filters: {
        ...defaultFilters,
        userId,
        username: intl.formatMessage(messages.AssignedToMe),
        withTags: initialSearchParams.withTags,
      },
      sort: 'deadline',
      desc: false,
    });
  };

  closeDialogs = () => this.setState({ currentModal: null });

  onTaskDeleted = task =>
    this.setState(({ items }) => ({ items: items.filter(x => x.id !== task.id) }));

  withTagsChanged = e => {
    const { initialSearchParams } = this.state;
    const withTags = e && e.length ? e.map(x => x.id) : [];
    userStorage.setItem(WithTagsStorageKey, withTags);
    this.setState({
      initialSearchParams: {
        ...initialSearchParams,
        withTags,
      },
    });
  };

  renderRow = task => {
    const { intl } = this.props;
    const now = moment.utc();
    const deadlineUtc = moment.utc(task.deadline);

    return (
      <tr key={task.id}>
        <td>
          {task.deadline ? (
            <FormattedDateShort
              className={classNames({
                orange: deadlineUtc.isAfter(now) && deadlineUtc.isBefore(now.add(1, 'weeks')),
                red: now.isAfter(deadlineUtc),
              })}
              value={task.deadline}
            />
          ) : null}
        </td>
        <td>
          <div className="flex-row-nowrap">
            <TaskStatusToggle
              id={`TaskStatusToggle-${task.id}`}
              value={{
                id: task.id,
                status: task.status,
                isBlocked: task.isBlocked,
                inapplicable: task.inapplicable,
                rejected: task.rejected,
                hasAttachments: task.hasAttachments,
                hasRemark: task.hasRemark,
                taskLevel: 2,
              }}
              onTasksUpdated={this.handleUpdateTasks}
              isMainTask
              title={intl.formatMessage(messages.ChangeTodoStatus)}
              onOpenDetail={() => this.openModal({ type: 'taskDetail', taskId: task.id })}
            />
            <span className="text-nowrap overflow-ellipsis flex-item">
              <a
                onClick={() => this.openModal({ type: 'taskDetail', taskId: task.id })}
                title={intl.formatMessage(messages.OpenDetails)}
              >
                {task.name}
              </a>
            </span>
          </div>
        </td>
        <td>
          {task.relation && task.relation.id ? <RelationName relation={task.relation} /> : null}
        </td>
        <td>
          <CanCreate on="RegistrationOwn">
            <a
              className="btn btn-default btn-xs btn-icon"
              title={intl.formatMessage(messages.AddRegistration)}
              onClick={() =>
                this.openModal({
                  type: 'registration',
                  modalProps: getRegistrationModalPropsForTask(task),
                })
              }
            >
              <FaRegClock />
            </a>
          </CanCreate>
          <a
            className="btn btn-default btn-xs btn-icon"
            title={intl.formatMessage(messages.OpenDetails)}
            onClick={() => this.openModal({ type: 'taskDetail', taskId: task.id })}
          >
            <FaRegWindowRestore />
          </a>

          <CanDelete on="Todo">
            <a
              className="btn btn-default btn-xs btn-icon"
              title={intl.formatMessage(messages.DeleteTodo)}
              onClick={() => this.openModal({ type: 'confirmDelete', taskId: task.id })}
            >
              <FaTrashAlt />
            </a>
          </CanDelete>
        </td>
      </tr>
    );
  };

  render() {
    const { items, currentModal, users, initialSearchParams } = this.state;
    const { intl } = this.props;

    return (
      <div className="todo-list-dashboard">
        <div className="title-block">
          <FormattedMessage {...messages.MyTodos} />
        </div>
        <div className="content-block">
          <ScrollableTable
            tableClassName="table-grid"
            loadItems={this.loadItems}
            initialSearchParams={initialSearchParams}
            items={items}
            useWindow={false}
            renderRow={this.renderRow}
            renderTBody
          >
            <ColumnHeader title={intl.formatMessage(messages.Deadline)} sortKey="deadline" />
            <ColumnHeader title={intl.formatMessage(messages.Description)} />
            <ColumnHeader title={intl.formatMessage(messages.Relation)} sortKey="relation" />
            <ColumnHeader />
          </ScrollableTable>
        </div>
        <div className="bottom-block">
          <CanCreate on="Todo">
            <Button
              btnClassName="btn btn-xs btn-inverse"
              onClick={() => this.openModal({ type: 'createTodo' })}
            >
              <AddIcon />
              <span>{intl.formatMessage(messages.CreateNewToDo)}</span>
            </Button>
          </CanCreate>
          {initialSearchParams && (
            <div className="inline-flex">
              <span>{intl.formatMessage(messages.FilterTodos)}&nbsp;</span>
              <EditableTaskTagSelector
                value={initialSearchParams?.withTags || []}
                onChange={e => this.withTagsChanged(e)}
              />
            </div>
          )}

          {!!items && items.length ? (
            <div className="pull-right">
              <a onClick={this.openTaskList}>
                <FormattedMessage {...messages.OpenTodosInTasklist} />
              </a>
            </div>
          ) : null}
        </div>
        {currentModal && currentModal.type === 'createTodo' && (
          <CreateToDoModal show onSave={this.onToDoCreated} onClose={this.closeDialogs} />
        )}
        {currentModal && currentModal.type === 'taskDetail' && (
          <TaskDetailsModal
            show
            users={users}
            task={{ id: currentModal.taskId }}
            onSave={this.onCloseTaskDetail}
            onClose={this.closeDialogs}
            onTaskDeleted={this.onTaskDeleted}
          />
        )}
        {currentModal && currentModal.type === 'registration' && (
          <RegistrationModal
            {...currentModal.modalProps}
            show
            onSave={this.closeDialogs}
            onClose={this.closeDialogs}
          />
        )}
        {currentModal && currentModal.type === 'confirmDelete' && (
          <YesNoModal
            show
            title={intl.formatMessage(messages.DeleteTodo)}
            message={intl.formatMessage(messages.DeleteTodoConfirmationMsg)}
            onNo={this.closeDialogs}
            onSave={() => this.deleteTodo(currentModal.taskId)}
          />
        )}
      </div>
    );
  }
}

TodoListDashboard.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
};

export default injectStateService(injectIntl(TodoListDashboard));
